/**
 * Protocol clause catalog for BRD-CLIN-2026-006 (Emory CHF Escalation).
 * -----------------------------------------------------------------------------------------
 * Every `GuideEscalation.clause` in a seeded guide MUST be a key here — the seed validator rejects
 * anything else, so an alert can never reach the NCM queue citing a clause nobody can look up (§5.1).
 *
 * Ids are stable and MUST NOT be renamed: they are persisted on `vital_flags` documents and on the
 * `clinical_escalation_events` audit rows, and they are the join key for audit reporting. To correct
 * wording, edit `text` and leave the id alone. To retire a clause, keep the entry and mark it
 * `retired: true` so historical alerts still resolve.
 *
 * MIRRORED IN FOUR PLACES, and there is no shared package across the repos to hold it:
 *   1. this module (the render-time source of truth for welby-web);
 *   2. welby-firestore `maintenanceScripts/sprint-9-11-26/protocolClauses.js` (the seed-time validator,
 *      which is also where `clause_text` on the guide document is derived from);
 *   3. the `protocol_clause` values in `SeedVitalFlagClients/escalationRanges.js`;
 *   4. the `protocol_clause` values in `AnnotateChfVitalFlagClauses/chfRuleClauses.js` (`vital_flag_CHF`).
 * The id-set-equality specs on both sides are the only thing keeping those four honest — a drifting
 * catalog is how AC-5 fails quietly. Keep them.
 *
 * TEXT IS VERBATIM, WHICHEVER SECTION IT COMES FROM. The NCM acts under standing orders, so the quoted clause has to be the
 * signed protocol's wording, not a paraphrase of it (§5.3). Each `text` is the BRD row's
 * "Finding / Threshold" cell, an em dash, then its "Required System Behavior" cell, character for
 * character — including the BRD's ASCII ">=" / "<" and its "120 to 139" phrasing.
 *
 * §4.1, THE TWO §4.3 TITRATION CLAUSES, §4.2 ROW 9, AND §4.2's CEILING ROW. The rule is unchanged and
 * is the only thing that decides what belongs here: nothing goes in "ready for later", only when a
 * rule cites it.
 *
 * All four meet it. welby-functions matches the §4.3 and row 9 ids by exact string to decide which
 * follow-up duties a Care Call escalation owes, and that code is merged; the ceiling row is cited by
 * six guide fields — #124's three at-ceiling checks and the three that apply the same row to the dose
 * a doubling would produce. Every OTHER §4.2 row, and the rest of §4.3 (the Review Trigger, §4.3's own
 * Ceiling Dose, Post Escalation Monitoring, the weekend leg, the No Improvement leg) stay out, and the
 * spec asserts it.
 *
 * The "stays out" set is deliberately named by WHAT IS IN rather than by row number. An earlier
 * version enumerated "§4.2 rows 1-8 and 10", which was a claim about the ceiling row's position that
 * nothing here can check — and this header has now contradicted the catalog three separate times.
 *
 * §4.2 ROW 9 CHANGES WHAT THE NCM READS, and that matters most in THIS repo: `formatProtocolClause`
 * resolves from this catalog first and falls back to a stored `clause_text`, so row 9 alerts move off
 * the seeded paraphrase and onto the BRD's own sentence.
 *
 * TWO LABEL AXES, and an entry carries exactly one. §4.1's third column is the Low/Moderate/High
 * ACUITY scale (`tierLabel`); §4.2's is an URGENCY (`urgencyLabel`) — row 9 is 'Routine (24 to 72
 * hr)', its siblings 'Same day', 'Urgent', 'Emergent'. Row 9 first shipped in review as
 * `tierLabel: 'Low'`, documented as a placeholder; since `formatProtocolClause` prefers catalog text,
 * that was never latent — it would have been the first thing an NCM read on every row-9 alert,
 * understating a protocol row that says Routine. Corrected before it rendered.
 *
 * The §4.3 standing orders are recorded in the BRD as PENDING PHYSICIAN SIGNATURE and OI-001 is open
 * on the durations in their text. The ids name the TIER and never the duration, so a resolution is a
 * `text` edit here and in welby-firestore together plus a fingerprint re-pin.
 */

export interface ProtocolClause {
  /** Stable, permanent id. Persisted on alerts and audit rows — never rename one. */
  id: string;
  /** Source document, e.g. 'BRD-CLIN-2026-006'. */
  document: string;
  /** Section within the document, e.g. '4.1'. */
  section: string;
  /**
   * The BRD's tier word — Low / Moderate / High. Deliberately NOT the platform's chip label, which
   * reads Low / Medium / High: the clause quotes the protocol, the chip shows the platform tier.
   *
   * Present on §4.1 rows. Mutually exclusive with `urgencyLabel` — read both via `protocolClauseLabel`.
   */
  tierLabel?: string;
  /**
   * The BRD's urgency word, for sections whose third column is a DEADLINE rather than an acuity:
   * §4.2 carries 'Routine (24 to 72 hr)', 'Same day', 'Urgent', 'Emergent'.
   *
   * A separate field rather than a wider `tierLabel`, so no consumer can read an urgency as a
   * severity — which is what welby-firestore's `chfRuleClauses` does when it maps a rule's severity
   * onto its clause's tier. Mutually exclusive with `tierLabel`; the spec enforces it.
   */
  urgencyLabel?: string;
  /**
   * The BRD's TITRATION TRIGGER tier, for §4.3's deployment rows: Moderate, High.
   *
   * Separate from `tierLabel` even though the words collide, because they are different axes — §4.1's
   * is the acuity a finding is REPORTED at, §4.3's is the trigger tier a titration is AUTHORISED
   * under. While both used `tierLabel`, welby-firestore's `chfRuleClauses` guard compared a rule's
   * severity to `clause.tierLabel` and happily accepted a weight rule repointed at a titration
   * clause, because "moderate" equals "moderate". With a distinct field that comparison fails
   * structurally. Raised in review of welby-firestore #136.
   */
  triggerLabel?: string;
  /** Verbatim §4.1 row text shown to the NCM. */
  text: string;
  /** Retired clauses stay in the catalog so historical alerts still resolve. */
  retired?: boolean;
}

export const PROTOCOL_CLAUSE_DOCUMENT = 'BRD-CLIN-2026-006';

export const PROTOCOL_CLAUSES: Record<string, ProtocolClause> = {
  'BRD-CLIN-2026-006#4.1-low-weight': {
    id: 'BRD-CLIN-2026-006#4.1-low-weight',
    document: PROTOCOL_CLAUSE_DOCUMENT,
    section: '4.1',
    tierLabel: 'Low',
    text:
      'Weight gain <2 lbs/24 hr or <3 lbs over 3 days — MA documents and confirms measurement accuracy. ' +
      'Escalates to NCM only if the patient is symptomatic (edema, shortness of breath).',
  },
  'BRD-CLIN-2026-006#4.1-low-vitals': {
    id: 'BRD-CLIN-2026-006#4.1-low-vitals',
    document: PROTOCOL_CLAUSE_DOCUMENT,
    section: '4.1',
    tierLabel: 'Low',
    text:
      'SpO2 >95%; HR <100; SBP 120 to 139 / DBP 80 to 90 with new symptoms — MA documents and notifies NCM ' +
      'for clinical assessment; provider notification only if not self resolving.',
  },
  // §4.1 row 3 is ONE BRD row covering three distinct findings. It is split into three ids because the
  // guide routes them from three different fields and an audit row citing "row 3" could not say which
  // finding fired. The "Required System Behavior" half is the row's, verbatim, on all three.
  'BRD-CLIN-2026-006#4.1-low-diuretic-dose-change': {
    id: 'BRD-CLIN-2026-006#4.1-low-diuretic-dose-change',
    document: PROTOCOL_CLAUSE_DOCUMENT,
    section: '4.1',
    tierLabel: 'Low',
    text:
      'New diuretic dose change — MA documents and notifies NCM. NCM reviews and assesses for acuity ' +
      'upgrade to Moderate.',
  },
  'BRD-CLIN-2026-006#4.1-low-new-edema': {
    id: 'BRD-CLIN-2026-006#4.1-low-new-edema',
    document: PROTOCOL_CLAUSE_DOCUMENT,
    section: '4.1',
    tierLabel: 'Low',
    text: 'New edema — MA documents and notifies NCM. NCM reviews and assesses for acuity upgrade to Moderate.',
  },
  'BRD-CLIN-2026-006#4.1-low-adherence': {
    id: 'BRD-CLIN-2026-006#4.1-low-adherence',
    document: PROTOCOL_CLAUSE_DOCUMENT,
    section: '4.1',
    tierLabel: 'Low',
    text:
      'Outreach adherence 60 to 79% — MA documents and notifies NCM. NCM reviews and assesses for acuity ' +
      'upgrade to Moderate.',
  },
  'BRD-CLIN-2026-006#4.1-moderate-weight': {
    id: 'BRD-CLIN-2026-006#4.1-moderate-weight',
    document: PROTOCOL_CLAUSE_DOCUMENT,
    section: '4.1',
    tierLabel: 'Moderate',
    text:
      'Weight gain >=2 lbs/24 hr or >=3 lbs over 3 days — MA confirms measurement (time of day, minimal ' +
      'clothing) and reviews triggers (sodium intake, fluid intake, adherence). MA notifies NCM for every ' +
      // "in Section 4.3" dropped from otherwise-verbatim BRD text, by decision: it is a cross-reference
      // to a document the NCM does not have, and the clinical content ("this is also the diuretic
      // Review Trigger") survives without it. The one place this catalog knowingly departs from the
      // source wording — recorded here so it is not read as a transcription slip. Mirrored in
      // welby-firestore, and the fingerprint in both specs is re-pinned for it.
      'Moderate tier weight finding. This threshold is also the diuretic Review Trigger, so ' +
      'NCM notification is NOT conditional on symptoms.',
  },
  'BRD-CLIN-2026-006#4.1-high-weight': {
    id: 'BRD-CLIN-2026-006#4.1-high-weight',
    document: PROTOCOL_CLAUSE_DOCUMENT,
    section: '4.1',
    tierLabel: 'High',
    text:
      'Weight gain >=3 lbs/24 hr or >=5 lbs over 3 days — Findings at this tier are NCM owned on receipt. ' +
      'The system routes directly to NCM for same day clinical assessment, bypassing standard MA queue hold time.',
  },
  // ── §4.2 Provider Notification ────────────────────────────────────────────────────────────────
  // ROW 9 ONLY. welby-functions `DOSE_ESCALATION_CLAUSE` matches this string whole to decide that a
  // dose escalation owes the PCP and BMP duties, so it meets the same admission rule as the §4.3 pair.
  //
  // NOTE FOR THIS REPO SPECIFICALLY: `formatProtocolClause` prefers catalog text over the stored
  // `clause_text`, so adding this entry CHANGES what the NCM reads on row 9 alerts — from the seeded
  // paraphrase ("Section 4.2 - any oral diuretic dose escalation requires...") to the BRD's own
  // sentence below. That swap is the decision this entry carries, not a side effect of it.
  //
  // No terminal period: §4.2's "Required System Behavior" column is unpunctuated, like §4.3's.
  'BRD-CLIN-2026-006#4.2-dose-escalation-monitoring': {
    id: 'BRD-CLIN-2026-006#4.2-dose-escalation-monitoring',
    document: PROTOCOL_CLAUSE_DOCUMENT,
    section: '4.2',
    urgencyLabel: 'Routine (24 to 72 hr)',
    text:
      'Any oral diuretic dose escalation event — Cardiology team notified to trigger a BMP within 7 days; ' +
      'PCP notified within 24 hours of the titration or deployment event',
  },

  // §4.2's CEILING row. MIRRORS welby-firestore `maintenanceScripts/sprint-9-11-26/protocolClauses.js`.
  // Catalogued because six guide fields cite it: #124's three at-ceiling checks, and the three
  // forward-looking ones that apply the same row to the dose a doubling would produce.
  //
  // IT CHANGES WHAT THE NCM READS ON THREE LIVE ROWS, which is this repo's half of the decision:
  // `formatProtocolClause` prefers catalog text, so the ceiling alerts stop rendering the seeded
  // paraphrase ("Section 4.2 - the diuretic ceiling dose is reached...") and start rendering the
  // BRD's own sentence. That is a welby-web release, not a configuration change — the stored
  // `clause_text` in Firestore is untouched and simply stops being reached.
  //
  // TERMINAL PERIOD, unlike row 9 above: this row's action cell is two sentences and ends with one.
  // §4.2 is not uniformly punctuated and each row is transcribed as written.
  //
  // "The system blocks additional NCM directed titration" is kept verbatim and is NOT literally
  // implemented — there is no order entry to intercept and the titration is a sentence spoken on a
  // call. What ships is detection plus a same-day task. The clause records what was asked for.
  'BRD-CLIN-2026-006#4.2-ceiling-dose': {
    id: 'BRD-CLIN-2026-006#4.2-ceiling-dose',
    document: PROTOCOL_CLAUSE_DOCUMENT,
    section: '4.2',
    urgencyLabel: 'Same day',
    text:
      'Diuretic ceiling dose reached (furosemide 160 mg/day, torsemide 200 mg/day, bumetanide 8 mg/day) — ' +
      'No further escalation without provider input. The system blocks additional NCM directed titration.',
  },

  // ── §4.3 Diuretic Deployment, Weight Triggered Logic ──────────────────────────────────────────
  // MIRRORS welby-firestore `maintenanceScripts/sprint-9-11-26/protocolClauses.js`. Only the two
  // TITRATION tiers are catalogued, and only because a rule cites them: welby-functions #374 matches
  // these exact strings to decide which post-titration monitoring duties a Care Call escalation owes.
  // The rest of §4.2 and the rest of §4.3 stay out until something cites them.
  //
  // NO TRAILING PERIOD, and that is the source rather than a slip: §4.3's "Authorized NCM Action"
  // column is unpunctuated throughout, where every §4.1 "Required System Behavior" cell ends with
  // one. Checked against the document, not a text render.
  //
  // OI-001 IS OPEN on the durations below (Moderate = 1 day, High = 2 days, with 2 and 3 under
  // discussion with Emory), and the standing orders are recorded in the BRD as pending physician
  // signature. The ids name the tier and never the duration, so a resolution is a `text` edit here
  // and in welby-firestore together, plus a fingerprint re-pin in both specs.
  // §4.3's WEEKEND row. MIRRORS welby-firestore. Cited by welby-functions' weekend branch on the
  // weight-trend path, which raises a `vital_flags` row when a 3-day gain lands in a sub-threshold
  // band on a Saturday or Sunday in the PATIENT's timezone. Nothing in this repo cites it — it is
  // here so the NCM queue can render its text.
  //
  // ONLY ONE OF ITS THREE ACTIONS IS IMPLEMENTED, and the text is verbatim so that stays visible:
  // the patient-directed dose increase is NOT built (the system will not instruct a patient to change
  // a dose — decided, not missed), the Monday NCM follow-up is what the alert is for, and the on-call
  // contact is guidance to the patient rather than a mechanism.
  //
  // `urgencyLabel` because this row is not a deployment tier; "Weekend" is the row's own name.
  'BRD-CLIN-2026-006#4.3-weekend-trending': {
    id: 'BRD-CLIN-2026-006#4.3-weekend-trending',
    document: PROTOCOL_CLAUSE_DOCUMENT,
    section: '4.3',
    urgencyLabel: 'Weekend',
    text:
      'Approaching threshold over a weekend, formal criteria not yet met — Patient directed dose increase ' +
      'per protocol, if BP and symptoms allow, plus Monday NCM follow up. Contact Emory on call for new or ' +
      'worsening weekend symptoms.',
  },

  // §4.3's NO IMPROVEMENT row. MIRRORS welby-firestore `maintenanceScripts/sprint-9-11-26/protocolClauses.js`.
  //
  // Its threshold column reads "Not applicable": there is no weight that triggers this. A clinician
  // determines at 48 to 72 hours that the oral escalation did not work, and the action says in as
  // many words that what follows is "outside Welby standing order scope" — so the platform records
  // and routes, and decides nothing.
  //
  // `urgencyLabel`, not `tierLabel`, unlike its two §4.3 siblings: those are named by the deployment
  // tier they belong to, and this row is not a tier. "48 to 72 hr" is taken from the row's own name.
  //
  // "(Tier 2)" inside the text is EMORY's escalation tier, not Welby's, and stays inside the sentence.
  'BRD-CLIN-2026-006#4.3-no-improvement': {
    id: 'BRD-CLIN-2026-006#4.3-no-improvement',
    document: PROTOCOL_CLAUSE_DOCUMENT,
    section: '4.3',
    urgencyLabel: '48 to 72 hr',
    text:
      'Not applicable, oral escalation ineffective — Escalate to Emory Clinical Pharmacist for IV diuresis ' +
      '(Tier 2), outside Welby standing order scope',
  },

  'BRD-CLIN-2026-006#4.3-moderate-titration': {
    id: 'BRD-CLIN-2026-006#4.3-moderate-titration',
    document: PROTOCOL_CLAUSE_DOCUMENT,
    section: '4.3',
    triggerLabel: 'Moderate',
    text: '>3 lbs over 3 days — 1 day of maintenance loop diuretic dose doubling',
  },
  'BRD-CLIN-2026-006#4.3-high-titration': {
    id: 'BRD-CLIN-2026-006#4.3-high-titration',
    document: PROTOCOL_CLAUSE_DOCUMENT,
    section: '4.3',
    triggerLabel: 'High',
    text: '>5 lbs over 3 days — 2 days of maintenance loop diuretic dose doubling',
  },
};

/** Every clause id in the catalog, retired ones included. The mirror specs compare against this. */
export const PROTOCOL_CLAUSE_IDS: string[] = Object.keys(PROTOCOL_CLAUSES);

/**
 * Catalog lookup for a clause id. Returns `undefined` for anything unknown — an alert carrying an
 * id the catalog has never heard of must render as nothing rather than as invented text, and the
 * seed validators are what stop such an alert existing in the first place (AC-5).
 */
export const findProtocolClause = (clauseId: string | undefined | null): ProtocolClause | undefined =>
  clauseId ? PROTOCOL_CLAUSES[clauseId] : undefined;

/**
 * The clause as the NCM reads it: `§4.1 Moderate — <verbatim text>`.
 *
 * `§` and the tier come from the catalog, not from the alert, so the same clause reads identically
 * on a guide-raised alert (which stores its text) and on an engine-raised one (which stores only the
 * id — §3.2b keeps the 500-character string off every vital flag on purpose).
 */
/**
 * Strips a leading BRD section reference off text that was SEEDED rather than catalogued.
 *
 * WHY THIS EXISTS. A catalogued clause never carries a section number — the catalog holds the BRD's
 * own row text and `formatProtocolClause` adds only the label. But nine guide fields cite clauses the
 * catalog does not hold, and their seeded `clause_text` opens with the reference:
 *
 *     "Section 4.2 - a weight gain of 5 lbs or more in under 48 hours requires same-day NCM contact…"
 *     "Section 4.2 rows 6 and 8 - emergency services or the ED were activated during the call…"
 *
 * Those strings render to clinicians, so they fall under the same rule as everything else: a nurse
 * cannot act on a section number. Catalogue a clause and the paraphrase stops rendering by itself,
 * which is how six of the original fifteen were already retired. The rest cannot be catalogued yet —
 * the catalog admits a clause only when a rule cites it, and these are cited by seeds on branches
 * this one cannot see — so the reference is removed at the point of RENDER instead.
 *
 * WHAT IT DOES NOT TOUCH, AND THE ONE THING IT DOES. The seeded `clause_text` in Firestore is
 * unchanged: no seed script runs, no guide version moves, and nothing rewrites clinical copy that was
 * signed off in its current wording. This is a display scrub, exactly as `formatProtocolClause`
 * already drops `§4.1` from catalogued text.
 *
 * But "display" is not the whole truth, and the distinction matters to anyone reading the audit
 * trail. `resolveClauseText` feeds the task body AND the `clinical_escalation_events` stamp, so from
 * this release the append-only audit row stores the SCRUBBED text. Rows written either side of the
 * deploy carry different `protocol_clause_text` for the same `protocol_clause` id. Nothing is
 * rewritten — the collection is append-only and earlier rows keep their wording — and the id, which
 * is what NFR-5.1 actually rests on, is identical throughout.
 *
 * Raised in review on #1261: the previous wording said "it does not touch what is stored", which is
 * the sentence the next reader would have relied on and been wrong about.
 *
 * DELIBERATELY NARROW, because it is a regular expression over clinical text. It is anchored at the
 * start, matches only `Section <n>[.<n>][ rows N, M and O]` followed by a dash OR A COLON, and
 * nothing else. The colon came from review: the 6-before/0-after measurement was taken against
 * `chf_ma` v2.19, where every string uses a hyphen, but another guide version could punctuate the
 * same prefix differently and one character covers it. A
 * sentence that merely mentions a section mid-string is untouched, and so is any text that does not
 * begin with the pattern. The capital is restored because the seeded sentences continue in lower
 * case after the prefix, and a rendered line starting "a weight gain of…" reads as a bug.
 */
const SEEDED_SECTION_PREFIX = /^Section\s+\d+(?:\.\d+)*(?:\s+rows?\s+\d+(?:(?:\s*,|\s+and)\s*\d+)*)?\s*[-\u2013\u2014:]\s*/i;

export const stripSeededSectionPrefix = (text: string): string => {
  const stripped = text.replace(SEEDED_SECTION_PREFIX, '');
  if (stripped === text || !stripped) {
    return text;
  }
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
};

export const formatProtocolClause = (clauseId: string | undefined | null, storedText?: string): string => {
  const clause = findProtocolClause(clauseId);
  // The scrub applies to the STORED half only: catalogued text never carries a section reference.
  const text = clause?.text ?? (storedText ? stripSeededSectionPrefix(storedText) : storedText);
  if (!text) {
    return '';
  }
  // NO SECTION NUMBER. It used to lead with `§4.1` / `§4.2`, which is how this codebase names the
  // requirement and means nothing to the nurse reading the queue (Valentina, from the live form:
  // "the nurses don't know what that means"). The label and the text carry everything clinical; the
  // section number carried only provenance.
  //
  // AUDITABILITY IS UNAFFECTED, which is what makes this safe to drop. The citation an auditor
  // follows is the `protocol_clause` ID stored on the alert, the task and the
  // `clinical_escalation_events` row — none of which is display text. NFR-5.1 is satisfied by the
  // stored id, never by this string.
  return clause ? `${protocolClauseLabel(clause)} — ${text}` : text;
};

/**
 * The one label an entry carries — its acuity tier (§4.1) or its urgency (§4.2).
 *
 * EXPORTED because reading `clause.tierLabel` directly is now a bug, and one that does not announce
 * itself: on a §4.2 entry it yields `undefined`, which template-literals into the string as the word
 * "undefined". That is how it reached the generated clinical note in review — `collectEscalations`
 * built `BRD-CLIN-2026-006 §4.2 undefined` for the §5.1 billing record. Every consumer goes through
 * here so the next §4.2 entry cannot repeat it.
 *
 * Returns '' rather than defaulting, for an entry carrying neither. The spec fails that case, so it
 * cannot ship; rendering nothing beats rendering a label the catalog does not hold.
 *
 * The `??` ORDER IS ARBITRARY and deliberately left so: the "exactly one label" invariant means no
 * entry can carry more than one, so reordering these reads changes no output. Noted because a
 * mutation of that order passes the whole suite, and the reason is the invariant rather than a
 * missing test.
 */
export const protocolClauseLabel = (clause: ProtocolClause): string =>
  clause.tierLabel ?? clause.triggerLabel ?? clause.urgencyLabel ?? '';

/**
 * Separator between the BRD row's two halves. Every `text` in this catalog is its "Finding /
 * Threshold" cell, this em dash, then its "Required System Behavior" cell — the shape the module
 * header describes and `care-call-guide-protocol-clauses.spec.ts` asserts for every entry.
 */
const CLAUSE_HALVES_SEPARATOR = ' — ';

/**
 * Just the reason the escalation fired: `Weight gain <2 lbs/24 hr or <3 lbs over 3 days`.
 *
 * The NCM queue's expansion row is one line in a dense table, and the full clause is two sentences —
 * the trigger followed by the "Required System Behavior" the platform has *already* carried out by
 * raising the alert. Rendering both put the instruction the NCM does not need in front of the
 * condition they do, and clamped the useful half out of view (Valentina Ariza, 2026-09-07: "para mi
 * seria dejar solo esto: Weight gain <2 lbs/24 hr or <3 lbs over 3 days").
 *
 * The tier is dropped with it — the row already carries a tier chip, so the tier word was saying it
 * twice. Nothing is lost: `formatProtocolClause` still composes the full `Low — <verbatim>` for the
 * tooltip on the same cell, and the clause **id** is what the audit trail joins on.
 *
 * (This sentence said `§4.1 Low — <verbatim>` until now. `formatProtocolClause` stopped emitting the
 * section number in 416de975, which updated the sibling comment in `client-flags-table.component.html`
 * and missed this one.)
 *
 * Derived rather than stored as a second field on purpose. This catalog is already mirrored in four
 * repos (see the module header) and a hand-typed copy of the first half of every `text` would be a
 * fifth mirror inside the same object — one that drifts the first time somebody corrects the wording
 * of `text` and does not think to correct its duplicate. The trade is that the derivation depends on
 * the separator, which is why the spec pins it for every entry instead of trusting it.
 *
 * Falls back to the whole text when there is no separator, so a clause worded differently renders
 * complete rather than empty.
 */
export const formatProtocolClauseTrigger = (clauseId: string | undefined | null, storedText?: string): string => {
  const text = findProtocolClause(clauseId)?.text ?? (storedText ? stripSeededSectionPrefix(storedText) : storedText);
  if (!text) {
    return '';
  }
  const separatorAt = text.indexOf(CLAUSE_HALVES_SEPARATOR);
  return separatorAt === -1 ? text : text.slice(0, separatorAt).trim();
};
